import {MarkerClusterer} from "@googlemaps/markerclusterer";
import "@plaudit/query-loop.api";

type AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;

type GoogleMapsConfig = {
	minimumMarkers: {
		count: number,
		types: string[]
	},
	maxSliceDistance: number, // In meters
	padding: number|google.maps.Padding,
	defaultBounds: google.maps.LatLngBoundsLiteral,
	iconTyper: (element: HTMLElement|string) => string,
	buildIcon(element: HTMLElement|string): Element
};
type MapElementClickedEventDetail = {
	marker: AdvancedMarkerElement,
	element: HTMLElement
}

const enum InfoWindowPositionResetMethod {
	NONE,
	RETURN,
	CLEAR
}

class InfoWindowManager {
	private readonly infoWindow = new google.maps.InfoWindow();

	public constructor(private readonly map: google.maps.Map, private readonly queryLoopForm: HTMLFormElement) {
		// We use drag instead of center_changed because we only want to detect user-initiated changes.
		map.addListener('drag', () => this.infoWindow.set('originalPosition', null));

		// This works because the click event will only trigger when the map itself is clicked.
		map.addListener('click', () => this.closeInfoWindow(InfoWindowPositionResetMethod.RETURN));
	}

	public openInfoWindow(marker: PositionedAdvancedMarkerElement, element: HTMLElement) {
		this.closeInfoWindow(InfoWindowPositionResetMethod.NONE, false);
		if (!this.infoWindow.get('originalPosition')) {
			this.infoWindow.set('originalPosition', this.map.getBounds());
		}
		const infoWindowContent = element.classList.contains('queryLoopGoogleMap__infoWindowContent') ? element : element.querySelector('.queryLoopGoogleMap__infoWindowContent');
		if (infoWindowContent) {
			this.infoWindow.setContent(infoWindowContent.cloneNode(true) as Element);
			this.infoWindow.open(this.map, marker);
		}
	}

	public closeInfoWindow(resetPosition = InfoWindowPositionResetMethod.NONE, clearSelectedClasses = true) {
		this.infoWindow.close();
		// Remove the selected class from all location elements when closing the info window
		if (clearSelectedClasses) {
			for (const locationElement of this.queryLoopForm.querySelectorAll<HTMLElement>('.wp-block-post-template .location')) {
				locationElement.classList.remove('location--selected');
			}
		}
		if (resetPosition) {
			if (this.infoWindow.get('originalPosition')) {
				if (resetPosition === InfoWindowPositionResetMethod.RETURN) {
					this.map.fitBounds(this.infoWindow.get('originalPosition'), 0);
				}
				this.infoWindow.set('originalPosition', null);
			}
		}
	}
}

class SearchIntegration {
	public marker?: AdvancedMarkerElement;
	public queryNameMatchingElements: HTMLElement[] = [];
	private searchLocationPosition: google.maps.LatLngLiteral|null|undefined = undefined;
	private searchNameParts?: string[];

	public constructor(private readonly queryLoopForm: HTMLFormElement) {}
	public getSearchedLocationPosition(update = false): google.maps.LatLngLiteral|null {
		if (!update && this.searchLocationPosition !== undefined) {
			return this.searchLocationPosition;
		}
		const searchedNameParts = this.getSearchedNameParts(true);
		if (searchedNameParts?.length) {
			const queryNameMatchingElements: HTMLElement[] = [];
			for (const element of this.queryLoopForm.querySelectorAll<HTMLElement>('[data-lat][data-lng]')) {
				const location = element.closest(".location");
				if (location) {
					location.classList.toggle("query-matched-name", false);
					const matchedPartsLength = this.computeMatchLength(element);
					element.setAttribute("data-matched-search-parts-length", matchedPartsLength.toString());
					if (matchedPartsLength > 0) {
						location.classList.toggle("query-matched-name", true);
						queryNameMatchingElements.push(element);
					}
				}
			}
			this.queryNameMatchingElements = queryNameMatchingElements;
		} else {
			for (const element of this.queryLoopForm.querySelectorAll<HTMLElement>('[data-lat][data-lng]')) {
				element.removeAttribute("data-matched-search-parts-length");
			}
		}

		let latLng: string[];
		const location = plaudit.queryLoop.forms.get(this.queryLoopForm).getField({type: 'location'})?.getValue().toString();
		if (this.queryNameMatchingElements.length === 0 && location && (latLng = location.substring(location.lastIndexOf(':') + 1)?.split(',')).length === 2) {
			this.searchLocationPosition = {lat: parseFloat(latLng[0]), lng: parseFloat(latLng[1])};
		} else {
			this.searchLocationPosition = null;
		}
		return this.searchLocationPosition;
	}

	public getSearchedNameParts(update = false) {
		if (this.searchNameParts !== undefined && !update) {
			return this.searchNameParts;
		}
		const location = plaudit.queryLoop.forms.get(this.queryLoopForm).getField({type: 'location'})?.getValue().toString();
		if (location && location.indexOf(':') > -1) {
			return this.searchNameParts = location.substring(0, location.indexOf(':'))
				.replace(/[^a-zA-Z0-9\s]+/gi, ' ').toLowerCase().split(/\s+/);
		}
		return this.searchNameParts = [];
	}

	/**
	 * @param element - the Element with the data-lat and data-lon attributes for the location
	 */
	public getMatchLength(element: Element): number {
		const attrValue = element.getAttribute("data-matched-search-parts-length");
		if (attrValue === null) {
			const matchLength = this.computeMatchLength(element);
			element.setAttribute("data-matched-search-parts-length", matchLength.toString());
			return matchLength;
		}
		return attrValue ? parseInt(attrValue) : 0;
	}
	private computeMatchLength(element: Element): number {
		const headerText = element.querySelector<HTMLElement>(".wp-block-post-title")?.innerText
			.replace(/[^a-zA-Z0-9\s]+/gi, ' ').toLowerCase().split(/\s+/);
		if (headerText) {
			const searchedNameParts = this.getSearchedNameParts();
			if (!searchedNameParts.length) {
				return element.getAttribute("data-location-offerings")?.includes("urgent-care") ? 1000 : 0;
			}
			return searchedNameParts
				.filter(part => headerText.includes(part))
				.reduce((sum, str) => sum + str.length, 0);
		}
		return 0;
	}
}

type PositionedAdvancedMarkerElement = Omit<AdvancedMarkerElement, 'position'> & { position: NonNullable<AdvancedMarkerElement['position']> };
const isPositionedAdvancedMarkerElement = (e: any): e is PositionedAdvancedMarkerElement => e instanceof google.maps.marker.AdvancedMarkerElement && !!e.position;
type ManagedMarker = { marker: PositionedAdvancedMarkerElement, element: HTMLElement, matchLength: number };
type ManagedMarkerWithDistance = ManagedMarker & { distance: number };
type ManagedMarkersList = (ManagedMarker|ManagedMarkerWithDistance)[];

class MarkerHandler {
	private managedMarkers: ManagedMarkersList = [];
	private queryNameMatchingMarkers: ManagedMarkersList = [];
	private readonly clusterer: MarkerClusterer;
	private searchedLocationMarker: AdvancedMarkerElement|undefined;

	public constructor(
		private readonly queryLoopForm: HTMLFormElement,
		private readonly googleMapsConfig: GoogleMapsConfig,
		private readonly infoWindowManager: InfoWindowManager,
		private readonly searchIntegration: SearchIntegration,
		private readonly markerConstructor: new (args?: google.maps.marker.AdvancedMarkerElementOptions) => AdvancedMarkerElement,
		private readonly mapElement: HTMLElement,
		map: google.maps.Map
	) {
		this.clusterer = new MarkerClusterer({
			map,
			onClusterClick(event, cluster, map) {
				infoWindowManager.closeInfoWindow(InfoWindowPositionResetMethod.CLEAR);
				map.fitBounds(
					cluster.markers?.filter(isPositionedAdvancedMarkerElement)
						.reduce((bounds, marker) => bounds.extend(marker.position), new google.maps.LatLngBounds())
					?? new google.maps.LatLngBounds(),
					googleMapsConfig.padding
				);
			}
		});

		map.addListener('bounds_changed', () => {
			const bounds = map.getBounds() ?? new google.maps.LatLngBounds();
			for (const {marker, element} of this.managedMarkers) {
				element.closest(".location")?.classList.toggle("marker-is-not-in-bounds", !bounds.contains(marker.position));
			}
		});

		const searchedLocationMarker = this.makeSearchedLocationMarker();
		if (searchedLocationMarker) {
			this.clusterer.addMarker(searchedLocationMarker, true);
		}
	}

	public makeSearchedLocationMarker() {
		const searchedLocationPosition = this.searchIntegration.getSearchedLocationPosition(true);
		if (searchedLocationPosition) {
			return this.searchedLocationMarker = new this.markerConstructor({
				position: searchedLocationPosition,
				content: this.googleMapsConfig.buildIcon('searched-location')
			});
		} else {
			return this.searchedLocationMarker = undefined;
		}
	}

	public getBoundsForManagedMarkers(bonusPosition?: ConstructorParameters<typeof google.maps.LatLngBounds>[0], customMarkers?: ManagedMarkersList) {
		const markers = customMarkers ?? this.managedMarkers;
		if (markers.length === 0) {
			return new google.maps.LatLngBounds(bonusPosition).union(this.googleMapsConfig.defaultBounds);
		}
		return markers.reduce((bounds, {marker}) => bounds.extend(marker.position), new google.maps.LatLngBounds(bonusPosition));
	}

	public getMinimumBounds(): google.maps.LatLngBounds {
		if (this.queryNameMatchingMarkers.length > 0) {
			this.alphabeticallySortMarkers();

			const bounds = new google.maps.LatLngBounds(this.queryNameMatchingMarkers[0].marker.position);
			for (let i = 1; i < this.queryNameMatchingMarkers.length; i++) {
				bounds.extend(this.queryNameMatchingMarkers[i].marker.position);
			}
			return bounds;
		}

		const searchedLatLng = this.searchIntegration.getSearchedLocationPosition();
		if (searchedLatLng) {
			if (this.googleMapsConfig.minimumMarkers.count >= this.managedMarkers.length) {
				return this.getBoundsForManagedMarkers(searchedLatLng);
			}
			const nameAndDistanceOrderedMarkers = this.managedMarkers
				.map<ManagedMarkerWithDistance>(managedMarker => ({
					...managedMarker,
					distance: Math.abs(google.maps.geometry.spherical.computeDistanceBetween(managedMarker.marker.position, searchedLatLng))
				}))
				.sort((a, b) => {
					return a.matchLength !== b.matchLength ? b.matchLength - a.matchLength : a.distance - b.distance;
				});

			for (const distanceOrderedMarker of nameAndDistanceOrderedMarkers) {
				// This updates the displayed order of elements in the results column so that they are sorted by distance
				distanceOrderedMarker.element.parentElement?.parentElement?.appendChild(distanceOrderedMarker.element.parentElement);
			}

			// If the closest marker is farther away than the maximum distance at which we will display a slice of the markers, show all of them alongside the searched marker
			if (nameAndDistanceOrderedMarkers[0].distance > this.googleMapsConfig.maxSliceDistance) {
				return this.getBoundsForManagedMarkers(searchedLatLng);
			}

			const minimumMarkers: ManagedMarkerWithDistance[] = [];
			for (const type of this.googleMapsConfig.minimumMarkers.types) {
				for (let i = 0; i < nameAndDistanceOrderedMarkers.length; i++) {
					if (this.googleMapsConfig.iconTyper(nameAndDistanceOrderedMarkers[i].element) === type) {
						minimumMarkers.push(...nameAndDistanceOrderedMarkers.splice(i, 1));
						break;
					}
				}
			}
			const remainingMinimumMarkerAmount = this.googleMapsConfig.minimumMarkers.count - minimumMarkers.length;
			if (remainingMinimumMarkerAmount > 0) {
				minimumMarkers.push(...nameAndDistanceOrderedMarkers.slice(0, Math.min(remainingMinimumMarkerAmount, nameAndDistanceOrderedMarkers.length)));
			}
			return this.getBoundsForManagedMarkers(searchedLatLng, minimumMarkers);
		}
		this.alphabeticallySortMarkers();

		return this.getBoundsForManagedMarkers();
	}

	private alphabeticallySortMarkers() {
		const alphabeticalOrderedMarkers = this.managedMarkers
			.map<[HTMLElement, number, string]>(({element, matchLength}) => {
				return [element, matchLength, element.querySelector<HTMLElement>(".wp-block-post-title")?.innerText ?? ""];
			})
			.sort((a, b) => a[1] !== b[1] ? b[1] - a[1] : a[2].localeCompare(b[2]));
		for (const [element] of alphabeticalOrderedMarkers) {
			// This updates the displayed order of elements in the results column so that they are sorted by name
			element.parentElement?.parentElement?.appendChild(element.parentElement);
		}
	}

	public buildMarker(element: HTMLElement): PositionedAdvancedMarkerElement {
		const {lat, lng} = element.dataset;
		if (!lat || !lng) {
			throw new Error('All marker-creating elements must have data-lat and data-lng attributes')
		}
		const marker = new this.markerConstructor({
			position: {
				lat: parseFloat(lat),
				lng: parseFloat(lng)
			},
			content: this.googleMapsConfig.buildIcon(element)
			//TODO: Should we calculate + include a title?
		}) as PositionedAdvancedMarkerElement;
		marker.addListener('click', () => {
			element.scrollIntoView({behavior: "smooth", block: "center"});
			for (const locationElement of this.queryLoopForm.querySelectorAll<HTMLElement>('.wp-block-post-template .location')) {
				locationElement.classList.remove('location--selected');
			}
			element.closest('.location')?.classList.add('location--selected');
			this.infoWindowManager.openInfoWindow(marker, element);
			this.mapElement.dispatchEvent(new CustomEvent<MapElementClickedEventDetail>('MapElementClicked', {detail: {marker, element}}));
		});
		return marker;
	}

	public refreshMarkers() {
		if (this.searchedLocationMarker) {
			this.clusterer.removeMarker(this.searchedLocationMarker, true);
		}
		this.queryNameMatchingMarkers = [];
		const searchedLocationMarker = this.makeSearchedLocationMarker();
		const newManagedMarkers: ManagedMarkersList = [];
		this.queryLoopForm.querySelectorAll<HTMLElement>('[data-lat][data-lng]').forEach(element => {
			const managedMarker: ManagedMarkersList[number] = {marker: this.buildMarker(element), element, matchLength: this.searchIntegration.getMatchLength(element)};
			newManagedMarkers.push(managedMarker);
			if (element.closest(".location")?.classList.contains("query-matched-name")) {
				this.queryNameMatchingMarkers.push(managedMarker);
			}
		});

		this.infoWindowManager.closeInfoWindow(InfoWindowPositionResetMethod.CLEAR);
		this.clusterer.removeMarkers(this.managedMarkers.map(({marker}) => marker), true);
		this.managedMarkers = newManagedMarkers;

		if (searchedLocationMarker) {
			this.clusterer.addMarker(searchedLocationMarker, true);
		}
		this.clusterer.addMarkers(newManagedMarkers.map(({marker}) => marker));
	}
}

document.addEventListener('plauditBase__mapInitialized', async () => {
	const {AdvancedMarkerElement} = await google.maps.importLibrary('marker') as google.maps.MarkerLibrary;
	const basePin = await fetch(new URL('/wp-content/themes/plaudit-theme-child/blocks/QueryLoopGoogleMap/map-marker.svg', document.baseURI))
		.then(icon => {
			if (!icon.ok) {
				throw new Error("Unable to download the map marker");
			}
			return icon.text();
		})
		.then(icon => new DOMParser().parseFromString(icon, 'image/svg+xml').documentElement);

	const defaultGoogleMapsConfig: GoogleMapsConfig = {
		minimumMarkers: {
			count: 3,
			types: ['urgent-care']
		},
		maxSliceDistance: 96_560.6, // 60 Miles
		padding: 20,
		defaultBounds: {north: 49.5, south: 43, east: -89, west: -97.5}, // A rough bounding box for Minnesota
		iconTyper(element) {
			if (typeof element === 'string') {
				return element;
			} else if (element.getAttribute('data-location-offerings')?.split(',').map(offering => offering.trim()).includes('urgent-care') ?? false) {
				return 'urgent-care';
			}
			return 'default';
		},
		buildIcon(element) {
			const icon = basePin.cloneNode(true) as Element;
			icon.setAttribute('data-type', typeof element === 'string' ? element : (element.getAttribute('data-type') ?? this.iconTyper(element) ?? 'default'));
			return icon;
		}
	}

	const setUpMap = (mapElement: HTMLElement) => {
		mapElement.classList.toggle('plaudit-query-loop-map--setup', true);
		const queryLoopForm = mapElement.closest('form');
		if (!queryLoopForm) {
			return;
		}

		const map = new google.maps.Map(mapElement, {
			//TODO: Configure Google Map options
			maxZoom: 17, //This is an entirely arbitrary value determined by asking, "does it look nice?".
			mapId: mapElement.getAttribute('data-map-id'),
			zoomControl: true,
			scaleControl: true,
			mapTypeControl: false,
			streetViewControl: false
		});
		const infoWindowManager = new InfoWindowManager(map, queryLoopForm);

		const googleMapsConfig = defaultGoogleMapsConfig; //TODO: This is just to allow per-map configuration if needed

		const searchIntegration = new SearchIntegration(queryLoopForm);

		const markerHandler = new MarkerHandler(queryLoopForm, googleMapsConfig, infoWindowManager, searchIntegration, AdvancedMarkerElement, mapElement, map);

		const refreshMarkersAndMap = () => {
			markerHandler.refreshMarkers();
			map.fitBounds(markerHandler.getMinimumBounds(), googleMapsConfig.padding);
		};
		(window as any).map = map;

		document.addEventListener('queryLoopFormValueChange', () => refreshMarkersAndMap());
		document.addEventListener('queryLoopFormInitialized', () => refreshMarkersAndMap());
		refreshMarkersAndMap();
	};

	new MutationObserver(mutations => {
		for (const mutation of mutations) {
			for (const addedNode of mutation.addedNodes) {
				if (addedNode instanceof HTMLElement) {
					addedNode.querySelectorAll<HTMLElement>('.plaudit-query-loop-map').forEach(setUpMap);
				}
			}
		}
	}).observe(document, {childList: true, subtree: true});

	document.querySelectorAll<HTMLElement>('.plaudit-query-loop-map').forEach(setUpMap);
});
